import { createRef, useContext, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleEventModeration } from "actions/events/event-moderation";
import axios from "axios";
import InputTextArea from "components/molecules/InputTextArea";
import { Button } from "components/ui/button";
import Loader from "componentsV2/atoms/Loadings/Loader";
import ConfirmDeleteModal from "componentsV2/molecules/Modals/ConfirmDeleteModal";
import EventsContext from "context/EventsListingsProvider";
import MoveIcon from "public/images/move-icon.svg";
import MoveOutIcon from "public/images/move-out-icon.svg";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { TAGS } from "utils/caching";

import styles from "./style.module.scss";

export default function EventModeration({
  isOpen,
  setIsOpen,
  setRerender,
  rerender,
  changeSetOpen,
  partnerSlug,
  userPermissions,
  refetchEvents,
}) {
  const queryClient = useQueryClient();
  const ref: any = createRef();
  const { t } = useTranslation();
  const onClose = () => setIsOpen(false);
  const onToggle = () => setIsOpen(!isOpen);

  const { selectedEvent, setSelectedEvent } = useContext(EventsContext);

  const [eventComentary, setEventComentary] = useState(
    selectedEvent.moderationComment
  );
  const [unsubmitedChangeEvent, setUnsubmitedChangeEvent] =
    useState(selectedEvent);
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const handleModerateAction = (action) => {
    setIsOpen(action === "REJECTED");
    setDeleteOpen(action === "ACCEPTED");
    setUnsubmitedChangeEvent((e) => ({
      ...e,
      contentStatus: action,
    }));
    setRerender(!rerender);
  };

  const { mutate: mutateSave, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const { ok, message, data } = await handleEventModeration(
        Number(unsubmitedChangeEvent.id),
        {
          event: {
            contentStatus: unsubmitedChangeEvent.contentStatus,
            moderationComment: eventComentary,
            title: unsubmitedChangeEvent.title,
            partnerId: unsubmitedChangeEvent.partnerId,
          },
          slug: partnerSlug,
        }
      );
      setSelectedEvent(null);
      if (!ok) throw new Error(message);
      return { ok, message, data };
    },
    onSuccess: async ({ data }) => {
      const tags = unsubmitedChangeEvent.partnerId
        ? [TAGS.partnerEvents(unsubmitedChangeEvent.partnerId)]
        : unsubmitedChangeEvent.locatorCompanyId
          ? [TAGS.providerEvents(unsubmitedChangeEvent.locatorCompanyId)]
          : [TAGS.events];
      setDeleteOpen(false);

      queryClient.invalidateQueries({
        queryKey: tags,
      });

      toast.success(`Event '${selectedEvent?.title}' was moderated.`);
      queryClient.invalidateQueries({
        queryKey: ["hasModerationEvents"],
      });
      refetchEvents();
      changeSetOpen(false);
      onClose();

      const sendEmail =
        unsubmitedChangeEvent.contentStatus === "REJECTED" &&
        selectedEvent.contentStatus === "REQUIRED_REVIEW";
      if (!sendEmail) return;

      try {
        const url = unsubmitedChangeEvent.partnerId
          ? `/api/sendEmail/events/requiredChange`
          : `/api/sendEmail/provider/events/requiredChange`;

        const res = await axios.post(url, {
          eventId: selectedEvent.id,
          title: selectedEvent.title,
          ...(unsubmitedChangeEvent.partnerId && {
            partnerId: unsubmitedChangeEvent.partnerId,
          }),
          ...(unsubmitedChangeEvent.locatorCompanyId && {
            providerId: unsubmitedChangeEvent.locatorCompanyId,
          }),
          partnerSlug,
        });

        if (res?.status !== 200) {
          toast.error(
            `Error while sending email. Please contact the administrator.`
          );
        }
      } catch (error) {
        toast.warning("Email notification was not sent.");
      }
    },
    onError: () => {
      toast.error("Error trying to moderate the event.");
    },
  });

  return (
    <>
      {isLoading ? <Loader /> : null}
      <div
        className={`${styles.moderation_container}  ${
          isOpen ? styles.isOpen : ""
        }`}
        ref={ref}
        style={
          selectedEvent.contentStatus === "REJECTED"
            ? { borderTop: "5px solid #EB5757" }
            : {}
        }
      >
        <div className={`grid_container ${styles.header}`}>
          <span>
            {userPermissions.partnerEvents === "EDIT" ||
            userPermissions.providerEvents === "EDIT"
              ? unsubmitedChangeEvent.contentStatus === "REJECTED"
                ? `${t("update")} (${t("rejected")})`
                : t("in_moderation")
              : t("approve_event")}
          </span>
          <div className={styles.open_btn}>
            {userPermissions.partnerEvents === "FULL" ||
            userPermissions.providerEvents === "FULL" ? (
              <>
                <span
                  className={`${
                    unsubmitedChangeEvent.contentStatus === "ACCEPTED"
                      ? styles.accept_toggled
                      : styles.accept
                  }`}
                  onClick={() => {
                    handleModerateAction("ACCEPTED");
                  }}
                >
                  {t("accept")}
                </span>
                <span
                  className={`${
                    unsubmitedChangeEvent.contentStatus === "REJECTED"
                      ? styles.dismiss_toggled
                      : styles.dismiss
                  }`}
                  onClick={() => {
                    handleModerateAction("REJECTED");
                  }}
                >
                  {t("dismiss")}
                </span>
              </>
            ) : (
              <span className={styles.comments} onClick={() => onToggle()}>
                {t("comments")}
              </span>
            )}

            {isOpen ? (
              <div onClick={() => onToggle()}>
                <MoveOutIcon />
              </div>
            ) : (
              <div onClick={() => onToggle()}>
                <MoveIcon />
              </div>
            )}
          </div>
        </div>
        <div className={`${styles.content}`}>
          <h3>{t("mod_comments")}</h3>
          {userPermissions.partnerEvents === "FULL" ||
          userPermissions.providerEvents === "FULL" ? (
            <h5>{t("mod_comments_desc")}</h5>
          ) : null}
          <div className={styles.change_text}>
            {userPermissions.partnerEvents === "EDIT" ||
            userPermissions.providerEvents === "EDIT" ? (
              <div className={styles.mod_comments}>
                <span>{selectedEvent.moderationComment}</span>
              </div>
            ) : (
              <InputTextArea
                disabledComment={
                  unsubmitedChangeEvent.contentStatus !== "REJECTED"
                }
                id="contentChangeText"
                onChange={(e) => setEventComentary(e.target?.value)}
                placeholder={t("write_comm_event")}
                value={eventComentary}
              />
            )}
            {isOpen &&
            (userPermissions.partnerEvents === "FULL" ||
              userPermissions.providerEvents === "FULL") ? (
              <div className={styles.button_send}>
                <Button
                  className="pl-[7px]"
                  color="white"
                  icon="send"
                  onClick={() => {
                    setDeleteOpen(true);
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ConfirmDeleteModal
        description={
          unsubmitedChangeEvent.contentStatus === "REJECTED"
            ? t("rejected_to_calendar")
            : t("added_to_calendar")
        }
        handleAction={() => mutateSave()}
        isOpen={isDeleteOpen}
        loadingConfirm={isLoading}
        question={
          unsubmitedChangeEvent.contentStatus === "REJECTED"
            ? `${t("sure")} ${t("dismiss_lowercase")} ${t("this_event")}`
            : `${t("sure")} ${t("accept_lowercase")} ${t("this_event")}`
        }
        setOpen={setDeleteOpen}
      />
    </>
  );
}
